/* eslint-disable import/max-dependencies */
// @flow
import React, { type StatelessFunctionalComponent, type Element } from "react";
import { useDispatch } from "react-redux";
import { useTable, type TableProps } from "@fas/ui-framework/lib/services/table";
import {
  Table,
  IsActiveLabel,
  ViewLink,
  SelectSearchComponent,
  ShowMoreItems,
} from "@fas/ui-core";
import type { Column } from "@fas/ui-core/lib/Table/Table.types";
import { Box } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import type { SearchComponentProps } from "@fas/ui-core/lib/SearchComponents";
import externalRedirect from "../../components/App/ExternalRedirect";
import { MOTIVATION_CONTENTS_TABLE } from "../../helpers/constants";
import { getMotivationContentsSaga } from "../../actions/motivationContentsList";
import { getFields, compareById } from "../../services/utils";
import type { Classes, MotivationContent } from "./types/MotivationContentList.types";
import MotivationTemplatesColumn from "../../components/MotivationTemplatesColumn";

type Props = $ReadOnly<{|
  Actions?: Element<*>,
  choicedData?: Array<MotivationContent>,
  onSelect?: (MotivationContent) => mixed,
  onDeselect?: (MotivationContent) => mixed,
  onCreate?: () => mixed,
  allSelectAvailable?: () => boolean,
  rowSelectAvailable?: () => boolean,
|}>;

const useStyles: () => Classes = makeStyles((): {[$Keys<Classes>]: *} => ({
  fixedSizeColumn: {
    width: "20%",
  },
}));

const handleCreate: () => null = () => externalRedirect({ to: "/webpush/create", target: "_self" });

const MotivationContentsList: StatelessFunctionalComponent<Props> = ({
  Actions,
  choicedData,
  onSelect,
  onDeselect,
  onCreate = handleCreate,
  allSelectAvailable = (): boolean => true,
  rowSelectAvailable = (): boolean => true,
}) => {
  const classes: Classes = useStyles();
  const tableProps: TableProps = useTable(MOTIVATION_CONTENTS_TABLE);
  const dispatch: <A>(A) => A = useDispatch();

  const columns: Column<MotivationContent>[] = [
    {
      label: "Status",
      field: "isActive",
      searchable: true,
      SearchComponent: (props: SearchComponentProps): Element<*> => (
        <SelectSearchComponent
          {...props}
          label="Select"
          values={[
            { value: "", title: "None" },
            { value: "true", title: "Active" },
            { value: "false", title: "Inactive" },
          ]}
        />
      ),
      render: IsActiveLabel,
    },
    {
      label: "Id",
      field: "id",
    },
    {
      label: "Name",
      field: "name",
      searchable: true,
      render: (row: MotivationContent): Element<*> => <ViewLink {...row} viewPath="/webpush/view/:id" />,
    },
    {
      label: "Vis. Type",
      field: "visualisationType",
      searchable: true,
      render: (row: MotivationContent): Element<*> => <>{row.visualisationType}</>,
    },
    {
      field: "motivationTemplates",
      label: "Motivation Templates",
      className: classes.fixedSizeColumn,
      render: MotivationTemplatesColumn,
    },
    {
      field: "BrowserLanguages",
      label: "Browser Languages",
      searchable: true,
      className: classes.fixedSizeColumn,
      render: (row: MotivationContent): Element<*> => <ShowMoreItems data={getFields(row.BrowserLanguages, "language")} />,
    },
  ];

  const onLoad: () => mixed = () => dispatch(getMotivationContentsSaga());

  return (
    <Box m={2}>
      <Table
        {...tableProps}
        columns={columns}
        title="WebPush Motivation Content"
        onLoad={onLoad}
        onCreate={onCreate}
        selected={choicedData || tableProps.selected}
        onSelect={onSelect || tableProps.onSelect}
        onDeselect={onDeselect || tableProps.onDeselect}
        rowSelectAvailable={rowSelectAvailable}
        allSelectAvailable={allSelectAvailable}
        compareRows={compareById}
        Actions={Actions}
      />
    </Box>
  );
};

export default MotivationContentsList;
